import SlideLinkWithPreview from 'components/atoms/SlideLinkWithPreview';
import { SxProps, Theme } from '@mui/material';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  isError?: boolean;
}

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------

export const AVATAR_SIZE = 28;
export const ICON_SIZE = 16;

export const avatarSx = (bgcolor: string): SxProps<Theme> => ({
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  bgcolor,
  mt: 0.25,
});

export const markdownContentSx: SxProps<Theme> = {
  '& p': { margin: 0, mb: 0.5, '&:last-child': { mb: 0 } },
  '& ul, & ol': { mt: 0, mb: 0.5, pl: 2.5 },
  '& li': { mb: 0.25 },
  '& code': { fontSize: '0.8rem', bgcolor: 'action.hover', px: 0.5, py: 0.25, borderRadius: 0.5 },
  '& pre': {
    bgcolor: 'action.hover',
    p: 1,
    borderRadius: 1,
    overflow: 'auto',
    '& code': { bgcolor: 'transparent', p: 0 },
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 1, mb: 0.5, fontSize: '0.9rem', fontWeight: 600 },
  '& table': { borderCollapse: 'collapse', mb: 1, fontSize: '0.8rem' },
  '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5 },
  '& blockquote': { borderLeft: 3, borderColor: 'divider', pl: 1.5, ml: 0, color: 'text.secondary' },
  '& a': {
    color: 'primary.main',
    textDecoration: 'underline',
    textDecorationColor: 'rgba(190, 98, 165, 0.4)',
    '&:hover': { textDecorationColor: 'primary.main' },
  },
  fontSize: '0.875rem',
  lineHeight: 1.6,
  wordBreak: 'break-word',
  color: 'text.primary',
};

export const markdownOverrides = {
  overrides: {
    a: { component: 'a' as const, props: { target: '_blank', rel: 'noopener noreferrer' } },
    'slide-link': { component: SlideLinkWithPreview },
  },
};

export const actionCardSx: SxProps<Theme> = {
  pl: `${AVATAR_SIZE + 12}px`,
  pr: 0.5,
};

export const actionDetailMarkdownSx: SxProps<Theme> = {
  mt: 0.25,
  lineHeight: 1.5,
  overflowWrap: 'anywhere',
  maxHeight: 120,
  overflowY: 'auto',
  scrollbarWidth: 'thin',
  fontSize: '0.75rem',
  color: 'text.secondary',
  '& p': { margin: 0, mb: 0.25, '&:last-child': { mb: 0 } },
  '& ul, & ol': { mt: 0, mb: 0.25, pl: 2 },
  '& li': { mb: 0 },
  '& code': { fontSize: '0.7rem', bgcolor: 'action.hover', px: 0.5, py: 0.15, borderRadius: 0.5 },
  '& pre': {
    bgcolor: 'action.hover',
    p: 0.75,
    borderRadius: 0.75,
    overflow: 'auto',
    '& code': { bgcolor: 'transparent', p: 0 },
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 0.5, mb: 0.25, fontSize: '0.75rem', fontWeight: 600 },
  '& strong': { fontWeight: 600 },
  '& a': {
    color: 'primary.main',
    textDecoration: 'underline',
    '&:hover': { textDecorationColor: 'primary.main' },
  },
};

export const actionListSx: SxProps<Theme> = {
  maxHeight: 420,
  overflowY: 'auto',
  py: 0.25,
  scrollbarWidth: 'thin',
};

export const fadeInKeyframes = {
  '@keyframes fadeInAction': {
    from: { opacity: 0, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
};

const fadeOutKeyframes = {
  '@keyframes fadeOutAction': {
    from: { opacity: 1 },
    to: { opacity: 0.45 },
  },
};

export const actionRowSx = (isRunning: boolean): SxProps<Theme> => ({
  mx: 0,
  px: 0,
  py: 0.75,
  ...fadeInKeyframes,
  ...(isRunning
    ? { animation: 'fadeInAction 0.35s ease-out' }
    : { ...fadeOutKeyframes, animation: 'fadeOutAction 0.4s ease-out forwards', opacity: 0.62 }),
});
